import { supabase } from '@/integrations/supabase/client';

// Shared, week-scoped React Query cache sources for the NFL staging surface (SAB-830 /
// cache-and-refetch). These hold CANONICAL raw rows so every consumer reads the SAME cache
// under the SAME key and derives its own shape client-side — Matchups and Sabotages both key
// off the current (league, week), so switching between them reuses the cache instead of
// refetching. Standings/Home use different scopes (season-wide, or context-owned team records)
// and keep their own keys.

export interface NFLMatchupRow {
  id: string;
  week: number;
  // SAB-188: a playoff matchup exists before its participants are known.
  home_team_id: string | null;
  away_team_id: string | null;
  home_score: number;
  away_score: number;
  status: string;
  winner_team_id: string | null;
}

export interface NFLSabotageEventRow {
  id: string;
  matchup_id: string | null;
  card_id: string;
  attacker_team_id: string;
  target_team_id: string;
  week: number;
  status: string;
}

// SAB-824: season is part of the key. Two leagues on different seasons, or one league
// across a rollover, must not share a cache entry.
export const nflMatchupsQueryKey = (leagueId?: string, week?: number, seasonYear?: number) =>
  ['nfl-matchups', leagueId, week, seasonYear] as const;

export async function fetchNflMatchups(
  leagueId: string, week: number, seasonYear: number,
): Promise<NFLMatchupRow[]> {
  // SAB-824: filtered on league_id + week ALONE until now, with no season dimension at
  // all. That is a live over-match, not a rollover hazard: prod league "Trash Test"
  // returned 12 rows for week 2 instead of 6, mixing its 2026/PRE rehearsal matchups
  // with its 2026/REG ones. Both pages that use this (NFLMatchupsPage, NFLSabotagesPage)
  // rendered the union.
  //
  // Missed by the SAB-824 enumeration, which searched for `.eq('season_year', ...)` --
  // a query with NO season filter is invisible to that search. Worth remembering: the
  // absent filter is harder to find than the wrong one.
  //
  // season_type REG is a deliberate scope choice, consistent with every other surface in
  // this sweep (locks, byes, the sabotage store, waivers, bonuses). The fantasy product
  // is a regular-season product; the only PRE matchups on prod are scoring-rehearsal
  // artifacts in one test league. If preseason matchups ever need to be user-visible,
  // this is the second real consumer of the SAB-901 current-phase resolver, alongside
  // the player game log (SAB-902) -- do it there rather than dropping the filter.
  const { data, error } = await supabase
    .from('nfl_matchups')
    .select('id, week, home_team_id, away_team_id, home_score, away_score, status, winner_team_id')
    .eq('league_id', leagueId)
    .eq('season_year', seasonYear)
    .eq('season_type', 'REG')
    .eq('week', week)
    .order('id');
  if (error) throw error;
  return (data || []) as NFLMatchupRow[];
}

export const nflSabotageEventsQueryKey = (leagueId?: string, week?: number) =>
  ['nfl-sabotage-events', leagueId, week] as const;

export async function fetchNflSabotageEvents(leagueId: string, week: number): Promise<NFLSabotageEventRow[]> {
  const { data, error } = await supabase
    .from('nfl_sabotage_events' as any)
    .select('id, matchup_id, card_id, attacker_team_id, target_team_id, week, status')
    .eq('league_id', leagueId)
    .eq('week', week)
    .eq('status', 'active');
  if (error) throw error;
  return (data || []) as unknown as NFLSabotageEventRow[];
}

// Sabotage card catalog (id -> name/rarity/…). Rarely changes, so a long staleTime; shared so
// Matchups' name enrichment and any store view read the same catalog rather than each fetching.
export const nflSabotageCardsQueryKey = ['nfl-sabotage-cards'] as const;

export async function fetchNflSabotageCards(): Promise<any[]> {
  const { data, error } = await supabase
    .from('nfl_sabotage_cards' as any)
    .select('id, name, description, rarity, category');
  if (error) throw error;
  return (data || []) as any[];
}

// A live game is any non-final matchup. Callers gate a polling refetchInterval on this so it
// runs only during the scoring window and goes silent once the week is entirely final.
export function hasLiveMatchup(rows: NFLMatchupRow[] | undefined): boolean {
  if (!rows) return false;
  return rows.some((m) => m.status !== 'final' && m.status !== 'completed');
}
